import api from '../services/api';

/**
 * THE FACTS AN APP'S STATE IS DERIVED FROM — one loader for every page that
 * calls utils/appState.js, so Home's pipeline and an app's own lifecycle strip
 * can never place the same app in two different states.
 *
 * Each source may fail on its own, and does so as "unknown", never as a
 * false negative:
 *   overview  → authorized users per app (all, and real people exactly), and
 *               the fleet totals. When it fails the per-app counts are absent
 *               (undefined), not zero, and `usersKnown` is false.
 *   scores    → the eval checks per app (runs the whole suite; can be slow)
 *   payout    → the developer's payout account; `undefined` when it could not
 *               be read (payments off on this instance, or an error), `null`
 *               when there is none. Telling someone to set up an account they
 *               may already have is worse than saying nothing.
 *
 * The scores call re-runs every app's checks, so the last good load of each
 * part is kept per group (a failed part is never kept): `{ reuse: true }`
 * (an app's own page) takes it while it is fresh instead of re-scoring the
 * fleet each time an app is opened, and
 * `{ refresh: ['overview'] }` re-reads only the parts an action changed.
 */
const PARTS = ['overview', 'scores', 'payout'];
const FRESH_MS = 2 * 60 * 1000;
// group key → { overview: {at, value}, scores: {at, value}, payout: {at, value} }.
// ONLY parts that loaded are held: a failed part is never cached, so the next
// load asks again instead of repeating "unknown" for two minutes.
const cache = new Map();

function fetchPart(part, groupId) {
  const q = groupId ? `?group_id=${groupId}` : '';
  if (part === 'overview') return api.request(`/third-party/my-apps/overview${q}`).catch(() => null);
  if (part === 'scores') return api.request(`/third-party/my-apps/scores${q}`).catch(() => null);
  return api.getPayoutAccount(groupId || undefined).then((res) => res?.account || null).catch(() => undefined);
}

/** A part that did not load: null for the fleet reads, undefined for payout (null there is "no account"). */
function failed(part, value) {
  return part === 'payout' ? value === undefined : value == null;
}

function counts(overview, field) {
  return Object.fromEntries((overview?.apps || [])
    .filter((a) => typeof a[field] === 'number')
    .map((a) => [String(a.app_id), a[field]]));
}

function build({ overview, scores, payout }) {
  return {
    usersByApp: Object.fromEntries((overview?.apps || []).map((a) => [String(a.app_id), a.authorized_users_count || 0])),
    // Exact real (non-test) people per app, counted by the backend the same way
    // /authorized-users counts them. Absent on an older backend → undefined.
    realUsersByApp: counts(overview, 'real_users_count'),
    // Test accounts holding a live grant: shown beside the real people, never added to them.
    testUsersByApp: counts(overview, 'test_users_count'),
    callsByApp: Object.fromEntries((overview?.apps || []).map((a) => [
      String(a.app_id), (a.calls_by_day_30d || []).reduce((n, d) => n + (d.count || 0), 0),
    ])),
    scoreByApp: Object.fromEntries((scores?.apps || []).map((s) => [String(s.id), s])),
    summary: overview?.summary || null,
    usersKnown: !!overview,
    account: payout,
    payoutReady: payout === undefined ? undefined : !!payout?.can_charge,
  };
}

/**
 * @param {string} [groupId]
 * @param {object} [opts]
 * @param {boolean} [opts.reuse]     take held parts for this group while they are fresh
 * @param {string[]} [opts.refresh]  re-read these parts, keeping the rest from the last load
 *
 * Whatever the options, a part with nothing held (never loaded, or its last
 * read failed) is always read again.
 */
export async function loadFleetSignals(groupId, { reuse = false, refresh } = {}) {
  const key = groupId || '';
  const held = cache.get(key) || {};
  const now = Date.now();
  const keep = (p) => {
    if (!held[p]) return false;
    if (reuse) return now - held[p].at < FRESH_MS;
    if (refresh) return !refresh.includes(p);
    return false;
  };

  const wanted = PARTS.filter((p) => !keep(p));
  const fetched = await Promise.all(wanted.map((p) => fetchPart(p, groupId)));

  const next = { ...(cache.get(key) || {}) };
  const parts = Object.fromEntries(PARTS.map((p) => [p, held[p]?.value]));
  wanted.forEach((p, i) => {
    parts[p] = fetched[i];
    if (failed(p, fetched[i])) delete next[p];
    else next[p] = { at: Date.now(), value: fetched[i] };
  });
  cache.set(key, next);
  return build(parts);
}

/** Drop every held load (tests, sign-out). */
export function forgetFleetSignals() {
  cache.clear();
}

/** The facts `describeApp` / `deriveState` / `deriveBlocking` take, for one app. */
export function factsFor(app, signals, draft) {
  return {
    draft: draft || null,
    score: signals?.scoreByApp?.[String(app.id)],
    authorizedUsers: signals?.usersByApp?.[String(app.id)],
    realUsers: signals?.realUsersByApp?.[String(app.id)],
    testUsers: signals?.testUsersByApp?.[String(app.id)],
    payoutReady: signals?.payoutReady,
  };
}
